import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@soroush/ui';
import { apiFetch } from '../lib/api';
import { toAirport } from '../lib/airports';
import { formatMoney, tehranIsoDate } from '../lib/format';
import { isoToJalali, monthName, toFaDigits, weekdayName } from '../lib/jalali';

const DAY_COUNT = 7;
const HORIZON_DAYS = 180;

type FareHit = { bookable?: boolean; totalFare: number | null };

const fareCache = new Map<string, number | null>();

function addIsoDays(iso: string, days: number): string {
  const [year, month, day] = iso.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day + days));
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  const d = String(date.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function windowDates(selected: string, shift: number, today: string): string[] {
  let start = addIsoDays(selected, -3 + shift);
  if (start < today) start = today;
  return Array.from({ length: DAY_COUNT }, (_, index) => addIsoDays(start, index));
}

export function lowestBookableFare(flights: FareHit[]): number | null {
  const fares = flights
    .filter((flight) => flight.bookable !== false && flight.totalFare != null && flight.totalFare > 0)
    .map((flight) => flight.totalFare as number);
  return fares.length ? Math.min(...fares) : null;
}

function dayLabel(iso: string, isFa: boolean): string {
  const j = isoToJalali(iso);
  if (!j) return iso;
  const raw = `${weekdayName(iso, isFa)} ${j.jd} ${monthName(j.jm, isFa)}`;
  return isFa ? toFaDigits(raw) : raw;
}

function summaryDate(iso: string, isFa: boolean): string {
  const j = isoToJalali(iso);
  if (!j) return iso;
  const raw = `${j.jd} ${monthName(j.jm, isFa)} ${j.jy}`;
  return isFa ? toFaDigits(raw) : raw;
}

export function SearchResultBar({
  origin,
  destination,
  date,
  passengers,
  editing,
  onEdit,
}: {
  origin: string;
  destination: string;
  date: string;
  passengers: number;
  editing: boolean;
  onEdit: () => void;
}) {
  const { t, i18n } = useTranslation();
  const isFa = i18n.language.startsWith('fa');
  const countLabel = t('common.passengerSummary', { count: passengers });

  return (
    <section className="search-summary">
      <div className="search-summary__route">
        <Place code={origin} label={t('common.origin')} isFa={isFa} />
        <PlaneIcon />
        <Place code={destination} label={t('common.destination')} isFa={isFa} />
        <p className="search-summary__date">
          <span>{t('common.departureDate')}</span>
          <strong>{summaryDate(date, isFa)}</strong>
        </p>
      </div>
      <div className="search-summary__rest">
        <p className="search-summary__pax">
          <span>{t('common.passengerCount')}</span>
          <strong>{isFa ? toFaDigits(countLabel) : countLabel}</strong>
        </p>
        <Button type="button" variant="ghost" aria-expanded={editing} onClick={onEdit}>
          {t('common.changeSearch')}
        </Button>
      </div>
    </section>
  );
}

function Place({ code, label, isFa }: { code: string; label: string; isFa: boolean }) {
  const airport = toAirport(code);
  const city = isFa ? airport.cityFa : airport.cityEn;
  const name = isFa ? airport.airportFa : airport.airportEn;
  return (
    <p className="search-summary__place">
      <span>{label}</span>
      <strong>
        {city}
        {name && name !== city ? <em>({name})</em> : null}
      </strong>
    </p>
  );
}

export function FareDateStrip({
  origin,
  destination,
  selected,
  cabin,
  passengers,
  children,
  infants,
  currency,
  knownFare,
  knownReady,
  onSelect,
}: {
  origin: string;
  destination: string;
  selected: string;
  cabin: string;
  passengers: string;
  children: string;
  infants: string;
  currency: string;
  knownFare: number | null;
  knownReady: boolean;
  onSelect: (iso: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const isFa = i18n.language.startsWith('fa');
  const today = useMemo(() => tehranIsoDate(new Date()), []);
  const [shift, setShift] = useState(0);
  const [fares, setFares] = useState<Record<string, number | null>>({});
  const dates = useMemo(() => windowDates(selected, shift, today), [selected, shift, today]);
  const datesKey = dates.join('|');

  useEffect(() => {
    setShift(0);
  }, [selected]);

  useEffect(() => {
    let cancelled = false;
    const keyFor = (date: string) =>
      [origin, destination, date, cabin, passengers, children, infants].join('|');
    const seeded: Record<string, number | null> = {};
    const todo: string[] = [];
    for (const date of dates) {
      if (knownReady && date === selected) {
        seeded[date] = knownFare;
        fareCache.set(keyFor(date), knownFare);
        continue;
      }
      const key = keyFor(date);
      if (fareCache.has(key)) seeded[date] = fareCache.get(key) ?? null;
      else todo.push(date);
    }
    setFares(seeded);

    for (const date of todo) {
      const qs = new URLSearchParams({
        origin,
        destination,
        departureDate: date,
        cabin,
        passengers,
      });
      if (children !== '0') qs.set('children', children);
      if (infants !== '0') qs.set('infants', infants);
      void apiFetch<{ results: FareHit[] }>(`/api/v1/search?${qs}`)
        .then((data) => lowestBookableFare(data.results))
        .catch(() => null)
        .then((fare) => {
          fareCache.set(keyFor(date), fare);
          if (!cancelled) setFares((prev) => ({ ...prev, [date]: fare }));
        });
    }
    return () => {
      cancelled = true;
    };
  }, [datesKey, origin, destination, cabin, passengers, children, infants, selected, knownReady, knownFare, dates]);

  const first = dates[0] ?? today;
  const last = dates[dates.length - 1] ?? today;

  return (
    <div className="fare-strip" role="tablist" aria-label={t('common.departureDate')}>
      <button
        type="button"
        className="fare-strip__arrow"
        aria-label={t('common.calendarPrevMonth')}
        disabled={first <= today}
        onClick={() => setShift((value) => value - 1)}
      >
        <Chevron />
      </button>
      <div className="fare-strip__days">
        {dates.map((date) => {
          const ready = Object.prototype.hasOwnProperty.call(fares, date);
          const fare = fares[date];
          const active = date === selected;
          return (
            <button
              key={date}
              type="button"
              role="tab"
              aria-selected={active}
              className={`fare-strip__day${active ? ' is-selected' : ''}${fare != null ? ' has-fare' : ''}`}
              onClick={() => {
                if (date !== selected) onSelect(date);
              }}
            >
              <span className="fare-strip__when">{dayLabel(date, isFa)}</span>
              {ready ? (
                fare != null ? (
                  <span className="fare-strip__price">{formatMoney(fare, currency, isFa)}</span>
                ) : (
                  <span className="fare-strip__miss">{t('common.dateUnavailable')}</span>
                )
              ) : (
                <span className="fare-strip__price fare-strip__price--wait" aria-hidden="true" />
              )}
            </button>
          );
        })}
      </div>
      <button
        type="button"
        className="fare-strip__arrow fare-strip__arrow--next"
        aria-label={t('common.calendarNextMonth')}
        disabled={last >= addIsoDays(today, HORIZON_DAYS)}
        onClick={() => setShift((value) => value + 1)}
      >
        <Chevron />
      </button>
    </div>
  );
}

function PlaneIcon() {
  return (
    <svg className="search-summary__plane" viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
      <path
        fill="currentColor"
        d="M21 16v-2l-8-5V3.5a1.5 1.5 0 0 0-3 0V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5z"
      />
    </svg>
  );
}

function Chevron() {
  return (
    <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
      <path fill="currentColor" d="M12.7 4.3 7 10l5.7 5.7-1.4 1.4L4.2 10l7.1-7.1z" />
    </svg>
  );
}
